import React, { FC, useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  DeleteTeam,
  fetchTeamList,
} from '../../../../states/containers/team/listSlice'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy, uniqueId } from 'lodash'
import { useAlert } from 'react-alert'
import TeamModal from '../../../components/setting/team/TeamModal'
import MiniMenu from '../../../components/setting/team/MiniMenu'
import ConfirmationModal from '../../../components/setting/team/ConfirmationModal'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import VideoGuide from '../../../components/common/VideoGuide'
import { compile } from '../../../../utils/search'

interface SimpleMember {
  id: number
  name: string
  email: string
  profile_image: string | null
  contract: string
  place: string
  active: boolean
}

interface SimpleTeam {
  team_id: number
  name: string
  code: string | null
  count: number
  created_at: string
  members: SimpleMember[]
  managers: SimpleMember[]
}

interface TeamModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetTeamData: SimpleTeam | null
}

interface MiniMenuState {
  display: boolean,
  x: number,
  y: number,
  id: number | undefined,
  status: string,
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const DISPLAY_BLOCK_NUMBER = 10

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const [isCloseable, setIsCloseable] = useState<boolean>(true)

  const [searchWord, setSearchWord] = useState<string>("")
  const [teams, setTeams] = useState<SimpleTeam[]>()
  const [teamsInit, setTeamsInit] = useState<SimpleTeam[]>()

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [isAllSearch, setIsAllSearch] = useState<boolean>(false)

  const [composition, setComposition] = useState<boolean>(false)

  const [fetchTrigger, setFetchTrigger] = useState<string>("")

  const {
    commonPart,
    teamList,
  } = useSelector((state: RootState) => state)

  const [teamModalState, setTeamModalState] = useState<TeamModalState>({
    display: false,
    type: "",
    id: undefined,
    targetTeamData: null,
  })

  const [miniMenuState, setMiniMenuState] = useState<MiniMenuState>({
    display: false,
    x: 0,
    y: 0,
    id: undefined,
    status: "",
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && (teams?.length || 0) >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, teams])

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    setIsCloseable(!commonPart.is_first_manager)
  }, [])

  useEffect(() => {
    dispatch(
      fetchTeamList(() => {
        setFetchTrigger(uniqueId())
      }),
    )
  }, [])

  const closeTeamModal = () => {
    setTeamModalState({
      ...teamModalState,
      targetTeamData: null,
      display: false,
    })
  }

  const addTeamCallback = () => {
    setSearchWord("")
    setIsAllSearch(true)
    setFetchTrigger(uniqueId())
  }

  const modTeamCallback = () => {
    setFetchTrigger(uniqueId())
  }

  const deleteTeam = (teamId: number, teamName: string) => {
    dispatch(
      DeleteTeam(
        teamId,
        () => {
          alert.success(t('setting.team.alert.delete_team', { name: teamName }))
          setFetchTrigger(uniqueId())
          closeTeamModal()
        },
      ),
    )
  }

  const reset = () => {
    setSearchWord("")
    setTeams(teamsInit)
    setDisplayBlockNum(1)
  }

  const search = (originalList: SimpleTeam[] | undefined) => {
    if (!searchWord) {
      reset()
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((team) => {
      if (team === null) return false
      return (compile(word).test(team.name + team.members.map((v) => v.name).join('') + team.managers.map((v) => v.name).join('')))
    }) as SimpleTeam[]
    setTeams(sortBy(list, 'team_id'))
  }

  const closeMiniMenu = () => {
    setMiniMenuState({
      ...miniMenuState,
      display: false,
    })
  }

  // confirmation modal
  const openConfirmationModal = (teamId: number, type: string) => {
    const teamData = teams?.find((v) => v.team_id === teamId)
    if (!teamData) return
    let callback = () => {
      // none
    }
    switch (type) {
      case "delete":
        callback = () => deleteTeam(teamId, teamData.name)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: teamData.name,
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openTeamModal = (id: number, type: string) => {
    const teamData = teams?.find((v) => v.team_id === id)
    setTeamModalState({
      ...teamModalState,
      display: true,
      type: type,
      id: id,
      targetTeamData: teamData || null,
    })
  }

  const updateTeam = (type: string) => {
    if (!miniMenuState.id) return
    openTeamModal(miniMenuState.id, type)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  useEffect(() => {
    if (!miniMenuState.display) return
    closeMiniMenu()
  }, [teamModalState.display, confirmationModalState.display])

  useEffect(() => {
    if (!fetchTrigger) return
    if (!teamList.teams) return
    if (!searchWord || isAllSearch) {
      setTeams(sortBy(teamList.teams, 'team_id'))
    } else {
      search(teamList.teams)
    }
    setTeamsInit(sortBy(teamList.teams, 'team_id'))
  }, [fetchTrigger, teamList.teams])

  useEffect(() => {
    if (teamList.isLoading || !fetchTrigger) return
    setFetchTrigger("")
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [teams])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - 共通設定 - ${t('setting.team.title')}`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        共通設定
        &nbsp;&gt;&nbsp;
        {t('setting.team.title')}
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          {t('setting.team.title')}
          <VideoGuide
            videoId="hA2cIraIfd8"
          />
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder={t('team.search.placeholder')}
              onChange={(event) => setSearchWord(event.target.value)}
              onBlur={() => search(teamsInit)}
              onKeyDown={(event) => keyDown(event)}
              onCompositionStart={() => setComposition(true)}
              onCompositionEnd={() => setComposition(false)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_description">
        <p>
          新規チームの作成、既存チームの編集、削除をすることができます。
        </p>
        <p>
          マネージャーとして登録されたユーザーは、チームメンバーに対して、カスタムアラートの設定やリスクアセスメントの参照をすることができます。
        </p>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              {t('setting.team.table_title')}
            </div>
            <div className="button-area">
              <button
                type="button"
                className="button add_team_button"
                onClick={(event) => openTeamModal(Number(event.currentTarget.dataset.id), "create")}
              >
                {t('setting.team.add_team')}
              </button>
            </div>
          </div>
          <div className="block-detail">
            {
              isLoading ? (
                <LoadingSquare>
                  <>
                    チーム情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  <table
                    id="hover-table"
                    className="flex-table team_list_table"
                  >
                    <thead>
                      <tr>
                        <th>
                          操作
                        </th>
                        <th>{t('model.team.name')}</th>
                        <th>
                          マネージャー・メンバー
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        teams && teams.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((team: SimpleTeam) => (
                          <tr key={uniqueId()}>
                            <td>
                              <div>
                                <div>
                                  <Tips
                                    description="チームを編集"
                                    className=""
                                    icon={false}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openTeamModal(team.team_id, "update")}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'tools']} />
                                    </button>
                                  </Tips>
                                </div>
                                <div>
                                  <Tips
                                    description="チームを削除"
                                    className=""
                                    icon={false}
                                  >
                                    <button
                                      type="button"
                                      className="del"
                                      onClick={() => openConfirmationModal(team.team_id, "delete")}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                                    </button>
                                  </Tips>
                                </div>
                              </div>
                            </td>
                            <td className="no_trans">
                              {team.name}
                              {
                                team.code && (
                                  <>
                                    {`(${team.code})`}
                                  </>
                                )
                              }
                            </td>
                            <td className="no_trans">
                              {
                                team.managers.length ? (
                                  <div className="mini_box">
                                    <div className="title">
                                      マネージャー
                                    </div>
                                    <div className="detail">
                                      {
                                        team.managers.map((v) => (
                                          <div
                                            key={uniqueId()}
                                          >
                                            {
                                              v.profile_image ? (
                                                <img
                                                  alt="profile"
                                                  src={v.profile_image}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                              ) : (
                                                <img
                                                  alt=""
                                                  src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                              )
                                            }
                                            {v.name}
                                            {
                                              v.active || (
                                                <Tips
                                                  description="休眠中💤"
                                                  className=""
                                                  icon={false}
                                                >
                                                  <>
                                                    💤
                                                  </>
                                                </Tips>
                                              )
                                            }
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                ) : (
                                  <></>
                                )
                              }
                              {
                                team.members.length ? (
                                  <div className="mini_box">
                                    <div className="title">
                                      メンバー
                                    </div>
                                    <div className="detail">
                                      {
                                        team.members.map((v) => (
                                          <div
                                            key={uniqueId()}
                                          >
                                            {
                                              v.profile_image ? (
                                                <img
                                                  alt="profile"
                                                  src={v.profile_image}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                              ) : (
                                                <img
                                                  alt=""
                                                  src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                              )
                                            }
                                            {v.name}
                                            {
                                              v.active || (
                                                <Tips
                                                  description="休眠中💤"
                                                  className=""
                                                  icon={false}
                                                >
                                                  <>
                                                    💤
                                                  </>
                                                </Tips>
                                              )
                                            }
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </div>
                                ) : (
                                  <></>
                                )
                              }
                            </td>
                          </tr>
                        ))
                      }
                      {/* {buildList} */}
                    </tbody>
                  </table>
                  {
                    !teams?.length && (
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "16px",
                        }}
                      >
                        <div
                          style={{
                            padding: "16px",
                            fontSize: "20px",
                            color: "#999999",
                          }}
                        >
                          チームがありません
                        </div>
                        <button
                          type="button"
                          className="button add_team_button"
                          onClick={(event) => openTeamModal(Number(event.currentTarget.dataset.id), "create")}
                        >
                          チームを作成しましょう
                        </button>
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
        {/* メニュー */}
        <MiniMenu
          miniMenuState={miniMenuState}
          closeCallback={closeMiniMenu}
          updateNameCallback={() => updateTeam("update_name")}
          updateMemberCallback={() => updateTeam("update_member")}
          updateManagerCallback={() => updateTeam("update_manager")}
          deleteCallback={() => openConfirmationModal(0, "delete")}
        />
        {/* 編集モーダル */}
        <TeamModal
          teamModalState={teamModalState}
          closeCallback={closeTeamModal}
          addCallback={addTeamCallback}
          modCallback={modTeamCallback}
          closeable={isCloseable}
        />
        {/* 確認モーダル */}
        <ConfirmationModal
          confirmationModalState={confirmationModalState}
          closeCallback={closeConfirmationModal}
        />
      </section>
    </section>
  )
}

export default Index
